import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import moment from 'moment';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import * as adminService from '../../services/admin';

const ImportExport = () => {
    const [activeTab, setActiveTab] = useState('import');
    const [importLogs, setImportLogs] = useState([]);
    const [exportLogs, setExportLogs] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(localStorage.getItem('branch_id') || '1');
    const [loading, setLoading] = useState(false);

    // Import state
    const [importType, setImportType] = useState('members');
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentImportLog, setCurrentImportLog] = useState(null);

    // Export state
    const [exportType, setExportType] = useState('members');
    const [exportFormat, setExportFormat] = useState('csv');
    const [exportFilters, setExportFilters] = useState({
        date_from: '',
        date_to: ''
    });

    useEffect(() => {
        if (activeTab === 'import') {
            fetchImportLogs();
        } else if (activeTab === 'export') {
            fetchExportLogs();
        }
    }, [activeTab, selectedBranch]);

    const fetchImportLogs = async () => {
        try {
            setLoading(true);
            const response = await adminService.getImportLogs({ branch_id: selectedBranch });
            if (response.status === 'success') {
                setImportLogs(response.data);
            }
        } catch (error) {
            toast.error('Failed to fetch import logs');
        } finally {
            setLoading(false);
        }
    };

    const fetchExportLogs = async () => {
        try {
            setLoading(true);
            const response = await adminService.getExportLogs({ branch_id: selectedBranch });
            if (response.status === 'success') {
                setExportLogs(response.data);
            }
        } catch (error) {
            toast.error('Failed to fetch export logs');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await adminService.downloadImportTemplate(importType);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${importType}_import_template.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Template downloaded successfully');
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUploadFile = async () => {
        if (!selectedFile) {
            toast.error('Please select a file to upload');
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('import_type', importType);
            formData.append('branch_id', selectedBranch);

            const response = await adminService.uploadImportFile(formData);

            if (response.status === 'success') {
                toast.success('File uploaded successfully');
                setCurrentImportLog(response.data);

                // Process import automatically
                await handleProcessImport(response.data.import_log_id);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to upload file');
        } finally {
            setLoading(false);
        }
    };

    const handleProcessImport = async (importLogId) => {
        try {
            setLoading(true);
            const response = await adminService.processImport(importLogId);

            if (response.status === 'success') {
                toast.success(`Import completed: ${response.data.success_rows} successful, ${response.data.failed_rows} failed`);

                if (response.data.errors && response.data.errors.length > 0) {
                    // Show errors in a modal
                    const errorHtml = response.data.errors.map(err =>
                        `<div>Row ${err.row}: ${err.error}</div>`
                    ).join('');

                    Swal.fire({
                        title: 'Import Errors',
                        html: errorHtml,
                        icon: 'warning',
                        width: 600
                    });
                }

                fetchImportLogs();
                setSelectedFile(null);
                setCurrentImportLog(null);
            }
        } catch (error) {
            toast.error('Failed to process import');
        } finally {
            setLoading(false);
        }
    };

    const handleExportData = async () => {
        try {
            setLoading(true);
            const response = await adminService.exportData({
                export_type: exportType,
                branch_id: selectedBranch,
                file_format: exportFormat,
                ...exportFilters
            });

            if (response.status === 'success') {
                toast.success('Export completed successfully');

                // Download the file
                const downloadResponse = await adminService.downloadExport(response.data.export_log_id);
                const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', response.data.file_name);
                document.body.appendChild(link);
                link.click();
                link.remove();

                fetchExportLogs();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to export data');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExport = async (exportLogId, fileName) => {
        try {
            const response = await adminService.downloadExport(exportLogId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('File downloaded successfully');
        } catch (error) {
            toast.error('Failed to download file');
        }
    };

    const handleDeleteImportLog = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will delete the import log and associated file',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await adminService.deleteImportLog(id);
                toast.success('Import log deleted successfully');
                fetchImportLogs();
            } catch (error) {
                toast.error('Failed to delete import log');
            }
        }
    };

    const handleDeleteExportLog = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will delete the export log and associated file',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await adminService.deleteExportLog(id);
                toast.success('Export log deleted successfully');
                fetchExportLogs();
            } catch (error) {
                toast.error('Failed to delete export log');
            }
        }
    };

    const importColumnDefs = [
        { field: 'id', headerName: 'ID', width: 80 },
        {
            field: 'created_at',
            headerName: 'Date',
            width: 150,
            valueFormatter: (params) => moment(params.value).format('YYYY-MM-DD HH:mm')
        },
        { field: 'import_type', headerName: 'Type', width: 120 },
        { field: 'file_name', headerName: 'File Name', width: 250 },
        { field: 'total_rows', headerName: 'Total', width: 100 },
        { field: 'success_rows', headerName: 'Success', width: 100 },
        { field: 'failed_rows', headerName: 'Failed', width: 100 },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            cellRenderer: (params) => {
                const statusClass = {
                    'completed': 'bg-success',
                    'failed': 'bg-danger',
                    'partial': 'bg-warning',
                    'processing': 'bg-info',
                    'pending': 'bg-secondary'
                };
                return (
                    <span className={`badge ${statusClass[params.value]}`}>
                        {params.value}
                    </span>
                );
            }
        },
        {
            field: 'user.name',
            headerName: 'User',
            width: 150,
            valueGetter: (params) => params.data.user?.name || 'N/A'
        },
        {
            headerName: 'Actions',
            width: 120,
            cellRenderer: (params) => (
                <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteImportLog(params.data.id)}
                >
                    Delete
                </button>
            )
        }
    ];

    const exportColumnDefs = [
        { field: 'id', headerName: 'ID', width: 80 },
        {
            field: 'created_at',
            headerName: 'Date',
            width: 150,
            valueFormatter: (params) => moment(params.value).format('YYYY-MM-DD HH:mm')
        },
        { field: 'export_type', headerName: 'Type', width: 150 },
        { field: 'file_format', headerName: 'Format', width: 100 },
        { field: 'file_name', headerName: 'File Name', width: 250 },
        { field: 'total_records', headerName: 'Records', width: 100 },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            cellRenderer: (params) => {
                const statusClass = {
                    'completed': 'bg-success',
                    'failed': 'bg-danger',
                    'processing': 'bg-info',
                    'pending': 'bg-secondary'
                };
                return (
                    <span className={`badge ${statusClass[params.value]}`}>
                        {params.value}
                    </span>
                );
            }
        },
        {
            headerName: 'Actions',
            width: 180,
            cellRenderer: (params) => (
                <div>
                    <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleDownloadExport(params.data.id, params.data.file_name)}
                    >
                        Download
                    </button>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteExportLog(params.data.id)}
                    >
                        Delete
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4>Data Import/Export</h4>
                        </div>
                        <div className="card-body">
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'import' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('import')}
                                    >
                                        Import Data
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'export' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('export')}
                                    >
                                        Export Data
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'import-logs' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('import-logs')}
                                    >
                                        Import Logs
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'export-logs' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('export-logs')}
                                    >
                                        Export Logs
                                    </button>
                                </li>
                            </ul>

                            {activeTab === 'import' && (
                                <div className="card">
                                    <div className="card-header">
                                        <h5>Import Data</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row mb-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Import Type</label>
                                                <select
                                                    className="form-control"
                                                    value={importType}
                                                    onChange={(e) => setImportType(e.target.value)}
                                                >
                                                    <option value="members">Members</option>
                                                    <option value="accounts">Accounts</option>
                                                    <option value="transactions">Transactions</option>
                                                    <option value="loans">Loans</option>
                                                    <option value="collections">Collections</option>
                                                    <option value="staff">Staff</option>
                                                </select>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">&nbsp;</label>
                                                <button
                                                    className="btn btn-info w-100"
                                                    onClick={handleDownloadTemplate}
                                                >
                                                    Download Template
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">Select File (CSV)</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept=".csv,.txt,.xlsx"
                                                onChange={handleFileSelect}
                                            />
                                            {selectedFile && (
                                                <div className="mt-2">
                                                    <small className="text-muted">
                                                        Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                                                    </small>
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            className="btn btn-primary"
                                            onClick={handleUploadFile}
                                            disabled={!selectedFile || loading}
                                        >
                                            {loading ? 'Processing...' : 'Upload and Import'}
                                        </button>

                                        {currentImportLog && (
                                            <div className="alert alert-info mt-3">
                                                <strong>Import in progress...</strong>
                                                <p>File: {currentImportLog.file_path}</p>
                                            </div>
                                        )}

                                        <div className="alert alert-warning mt-4">
                                            <strong>Instructions:</strong>
                                            <ul className="mb-0">
                                                <li>Download the template for your import type</li>
                                                <li>Fill in the data according to the template format</li>
                                                <li>Ensure all required fields are filled</li>
                                                <li>Upload the completed CSV file</li>
                                                <li>Review the import results and fix any errors</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'export' && (
                                <div className="card">
                                    <div className="card-header">
                                        <h5>Export Data</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row mb-3">
                                            <div className="col-md-4">
                                                <label className="form-label">Export Type</label>
                                                <select
                                                    className="form-control"
                                                    value={exportType}
                                                    onChange={(e) => setExportType(e.target.value)}
                                                >
                                                    <option value="members">Members</option>
                                                    <option value="accounts">Accounts</option>
                                                    <option value="transactions">Transactions</option>
                                                    <option value="loans">Loans</option>
                                                    <option value="collections">Collections</option>
                                                    <option value="vouchers">Vouchers</option>
                                                    <option value="reports">Reports</option>
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">File Format</label>
                                                <select
                                                    className="form-control"
                                                    value={exportFormat}
                                                    onChange={(e) => setExportFormat(e.target.value)}
                                                >
                                                    <option value="csv">CSV</option>
                                                    <option value="excel">Excel</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="row mb-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Date From (Optional)</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={exportFilters.date_from}
                                                    onChange={(e) => setExportFilters({...exportFilters, date_from: e.target.value})}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Date To (Optional)</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={exportFilters.date_to}
                                                    onChange={(e) => setExportFilters({...exportFilters, date_to: e.target.value})}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            className="btn btn-success"
                                            onClick={handleExportData}
                                            disabled={loading}
                                        >
                                            {loading ? 'Exporting...' : 'Export Data'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'import-logs' && (
                                <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                                    <AgGridReact
                                        rowData={importLogs}
                                        columnDefs={importColumnDefs}
                                        pagination={true}
                                        paginationPageSize={20}
                                    />
                                </div>
                            )}

                            {activeTab === 'export-logs' && (
                                <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                                    <AgGridReact
                                        rowData={exportLogs}
                                        columnDefs={exportColumnDefs}
                                        pagination={true}
                                        paginationPageSize={20}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportExport;
